import { useEffect, useRef, useState } from "react";
import { reviewApi, type ClientDetail, type ReviewClient } from "../../lib/review-api";
import type { Parcel } from "../../viewer/types";
import { OwnerStateBadge, ReviewBadge } from "./reviewShared";
import { EmbeddedClientGrid } from "./EmbeddedClientGrid";
import { fmtDate, shortId } from "./adminShared";
import { cn } from "../cn";

/**
 * CENTER pane — Clients. Every current legitimate OWNER of land (parcels and/or
 * Territory identities), grouped by wallet — NOT just publishers. Drill into a
 * client to see the REAL Grid framed on its parcels (interactive: zoom / pan /
 * live content). Clicking a parcel opens the moderation Inspector for the
 * Territory that owns it (or a "no published content" note for a bare parcel).
 * The textual Territory list stays below the grid as a quick index.
 *
 * `owner` (the selected client) is CONTROLLED by the parent so the workspace can
 * widen the left column for the embedded grid.
 */
export function ReviewClients({
  tick,
  owner,
  onOpenClient,
  selectedId,
  onSelect,
  onSelectBareParcel,
  onList,
}: {
  tick: number;
  owner: string | null;
  onOpenClient: (owner: string | null) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSelectBareParcel: (parcel: Parcel) => void;
  onList: (ids: string[]) => void;
}) {
  const [clients, setClients] = useState<ReviewClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const listReq = useRef(0);
  const detReq = useRef(0);

  useEffect(() => {
    const my = ++listReq.current;
    const c = new AbortController();
    setLoading(true);
    reviewApi
      .clients(200, c.signal)
      .then((res) => { if (my === listReq.current) { setClients(res.clients); setLoading(false); } })
      .catch(() => { if (!c.signal.aborted && my === listReq.current) { setClients([]); setLoading(false); } });
    return () => c.abort();
  }, [tick]);

  useEffect(() => {
    if (!owner) { setDetail(null); return; }
    const my = ++detReq.current;
    const c = new AbortController();
    setDetailLoading(true);
    reviewApi
      .clientDetail(owner, c.signal)
      .then((d) => { if (my === detReq.current) { setDetail(d); setDetailLoading(false); } })
      .catch(() => { if (!c.signal.aborted && my === detReq.current) { setDetail(null); setDetailLoading(false); } });
    return () => c.abort();
  }, [owner, tick]);

  useEffect(() => {
    onList(detail ? detail.territories.map((t) => t.identity_id) : []);
  }, [detail, onList]);

  if (owner) {
    return (
      <div data-testid="review-client-detail" className="panel-chamfer panel-chamfer--sm overflow-hidden">
        <div className="chamfer-strip">
          <button
            type="button"
            data-testid="review-client-back"
            onClick={() => onOpenClient(null)}
            className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] text-text-secondary hover:text-text-primary"
          >
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden><path d="M8.5 3.5L5 7l3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Clients
          </button>
          <span className="ml-auto font-mono text-[9px] text-text-muted break-all">{shortId(owner, 8, 6)}</span>
        </div>
        {detail && (
          <div className="px-4 py-2 border-b border-white/5 flex items-center gap-4 font-mono text-[9.5px] text-text-muted">
            <span data-testid="review-client-detail-parcels"><span className="text-text-secondary">{detail.parcels}</span> parcels</span>
            <span><span className="text-text-secondary">{detail.count}</span> territories</span>
          </div>
        )}
        {detail && detail.parcels_geom && detail.parcels_geom.length > 0 && (
          <div className="px-3 pt-3">
            <EmbeddedClientGrid
              parcels={detail.parcels_geom}
              territories={detail.territories}
              onPickTerritory={onSelect}
              onPickBareParcel={onSelectBareParcel}
            />
          </div>
        )}
        <div className="mt-1 max-h-[34vh] overflow-y-auto scroll-dark">
          {detailLoading && (
            <div data-testid="review-client-detail-loading" className="px-4 py-6 text-center font-mono text-[11px] text-text-muted">Loading…</div>
          )}
          {!detailLoading && detail && detail.territories.length === 0 && (
            <div data-testid="review-client-no-territories" className="px-4 py-8 text-center font-mono text-[11px] text-text-muted">
              Owns {detail.parcels} parcel{detail.parcels === 1 ? "" : "s"} · no Territory identity yet.
            </div>
          )}
          <ul className="divide-y divide-white/5">
            {detail?.territories.map((t) => (
              <li key={t.identity_id}>
                <button
                  type="button"
                  data-testid={`review-client-territory-${t.identity_id}`}
                  data-selected={selectedId === t.identity_id}
                  onClick={() => onSelect(t.identity_id)}
                  className={cn(
                    "w-full text-left px-4 py-3 transition-colors",
                    selectedId === t.identity_id ? "bg-[color:var(--spec-cyan-soft)]" : "hover:bg-white/[0.03]",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <ReviewBadge classification={t.classification} />
                    {t.moderated && <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-neon-coral">hidden</span>}
                    <span className="ml-auto font-mono text-[9px] text-text-muted">
                      {t.has_active_publication ? `rev ${t.current_publication_revision}` : `${t.parcel_count ?? 0} parcels`}
                    </span>
                  </div>
                  <div className="mt-1.5 text-[12px] text-text-primary truncate">
                    {t.display_name || <span className="font-mono text-[11px] text-text-secondary">{shortId(t.identity_id, 12, 8)}</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="review-clients" className="panel-chamfer panel-chamfer--sm overflow-hidden">
      <div className="chamfer-strip">
        <span className="strip-dot" aria-hidden />
        <span>Clients</span>
        <span className="ml-auto font-mono text-[9px] tracking-[0.14em] text-text-muted">{loading ? "…" : clients.length}</span>
      </div>
      <div className="max-h-[66vh] overflow-y-auto scroll-dark">
        {loading && (
          <div data-testid="review-clients-loading" className="px-4 py-6 text-center font-mono text-[11px] text-text-muted">Loading clients…</div>
        )}
        {!loading && clients.length === 0 && (
          <div data-testid="review-clients-empty" className="px-4 py-8 text-center font-mono text-[11px] text-text-muted">No current owners.</div>
        )}
        <ul className="divide-y divide-white/5">
          {clients.map((cl) => (
            <li key={cl.owner}>
              <button
                type="button"
                data-testid={`review-client-row-${cl.owner}`}
                onClick={() => onOpenClient(cl.owner)}
                className="w-full text-left px-4 py-3 transition-colors hover:bg-white/[0.03]"
              >
                <div className="flex items-center gap-2">
                  <OwnerStateBadge state={cl.status} />
                  <span className="ml-auto font-mono text-[9px] text-text-muted">{fmtDate(cl.latest_publish) || "—"}</span>
                </div>
                <div className="mt-1.5 font-mono text-[11px] text-text-primary break-all">{shortId(cl.owner, 10, 8)}</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[9.5px]">
                  <span className="text-text-secondary">{cl.parcels} parcels</span>
                  <span className="text-text-secondary">{cl.territories} territories</span>
                  {cl.pending > 0 && <span data-testid={`review-client-pending-${cl.owner}`} style={{ color: "#f0b429" }}>{cl.pending} pending</span>}
                  {cl.unpublished > 0 && <span className="text-text-muted">{cl.unpublished} unpublished</span>}
                  {cl.moderated > 0 && <span className="text-neon-coral">{cl.moderated} hidden</span>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
